import axios from "axios";
import { baseUrl } from "../util";

const productEpdBase = (productId) => `${baseUrl}/api/business/product/${productId}`;

/**
 * PATCH /api/business/product/<id>/lock-status
 * Body: { lca_lock_status?: "unlocked"|"calc_ended"|"epd_locked", calculation_finished_at?: boolean|"ISO8601" }
 */
export const patchProductLockStatus = ({
  productId,
  businessId,
  lca_lock_status,
  calculation_finished_at,
  onSuccess,
  onError,
}) => {
  const params = businessId ? { business_id: businessId } : {};
  const body = {};
  if (lca_lock_status !== undefined) body.lca_lock_status = lca_lock_status;
  if (calculation_finished_at !== undefined)
    body.calculation_finished_at = calculation_finished_at;

  axios
    .patch(`${productEpdBase(productId)}/lock-status`, body, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        "Content-Type": "application/json",
      },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * GET /api/business/product/<id>/epd-approval
 */
export const getProductEpdApproval = ({ productId, businessId, onSuccess, onError }) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .get(`${productEpdBase(productId)}/epd-approval`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * PUT /api/business/product/<id>/epd-approval
 * Body: { declaration_number, programme_operator, declaration_owner, issue_date, valid_to, verified_by, verification_date }
 * Rejected with 403 when product is EPD locked.
 */
export const updateProductEpdApproval = ({
  productId,
  businessId,
  data,
  onSuccess,
  onError,
}) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .put(`${productEpdBase(productId)}/epd-approval`, data, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        "Content-Type": "application/json",
      },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * GET /api/business/product/<id>/lca-versions
 */
export const getProductLcaVersions = ({ productId, businessId, onSuccess, onError }) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .get(`${productEpdBase(productId)}/lca-versions`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * GET /api/business/product/<id>/lca-versions/current
 */
export const getProductLcaVersionsCurrent = ({
  productId,
  businessId,
  onSuccess,
  onError,
}) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .get(`${productEpdBase(productId)}/lca-versions/current`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * POST /api/business/product/<id>/lca-versions/submit
 * Optional body: reviewer_approval, sign_off fields for persistence
 */
export const submitProductLcaVersion = ({
  productId,
  businessId,
  data = {},
  onSuccess,
  onError,
}) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .post(`${productEpdBase(productId)}/lca-versions/submit`, data, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        "Content-Type": "application/json",
      },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * POST /api/business/product/<id>/lca-versions/create-revision
 */
export const createProductLcaRevision = ({
  productId,
  businessId,
  onSuccess,
  onError,
}) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .post(`${productEpdBase(productId)}/lca-versions/create-revision`, {}, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        "Content-Type": "application/json",
      },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * GET /api/business/product/<id>/lca-versions/download-verification-package
 * Backend may return 501 Not Implemented; frontend should handle (e.g. show message or fallback).
 */
export const getProductLcaVerificationPackage = ({
  productId,
  businessId,
  onSuccess,
  onError,
}) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .get(`${productEpdBase(productId)}/lca-versions/download-verification-package`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
      params,
      responseType: "blob",
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * GET /api/business/product/<id>/dpp
 */
export const getProductDppStatus = ({ productId, businessId, onSuccess, onError }) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .get(`${productEpdBase(productId)}/dpp`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * GET /api/business/product/<id>/dpp/qr
 */
export const getProductDppQr = ({ productId, businessId, onSuccess, onError }) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .get(`${productEpdBase(productId)}/dpp/qr`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * POST /api/business/product/<id>/dpp/registry-retry
 */
export const retryDppRegistry = ({ productId, businessId, onSuccess, onError }) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .post(`${productEpdBase(productId)}/dpp/registry-retry`, {}, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        "Content-Type": "application/json",
      },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * POST /api/business/product/<id>/dpp — manual publish fallback
 */
export const publishProductDpp = ({ productId, businessId, onSuccess, onError }) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .post(`${productEpdBase(productId)}/dpp`, {}, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        "Content-Type": "application/json",
      },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * GET /api/business/product/<id>/dpp/documentation
 */
export const getProductDppDocumentation = ({
  productId,
  businessId,
  onSuccess,
  onError,
}) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .get(`${productEpdBase(productId)}/dpp/documentation`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * GET /api/business/product/<id>/dpp/editions
 */
export const getProductDppEditions = ({
  productId,
  businessId,
  onSuccess,
  onError,
}) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .get(`${productEpdBase(productId)}/dpp/editions`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * GET /api/business/product/<id>/dpp/events
 */
export const getProductDppEvents = ({
  productId,
  businessId,
  onSuccess,
  onError,
}) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .get(`${productEpdBase(productId)}/dpp/events`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/**
 * GET /api/business/product/<id>/dpp/units
 */
export const getProductDppUnits = ({
  productId,
  businessId,
  onSuccess,
  onError,
}) => {
  const params = businessId ? { business_id: businessId } : {};
  axios
    .get(`${productEpdBase(productId)}/dpp/units`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("access_token") },
      params,
    })
    .then((res) => onSuccess?.(res.data))
    .catch((err) => onError?.(err));
};

/** Unwrap `{ message, data }` or raw data from documentation APIs */
export const unwrapDppDocPayload = (res) => {
  if (!res) return null;
  if (res.data !== undefined && (res.message !== undefined || res.data?.current_edition || res.data?.results || Array.isArray(res.data))) {
    return res.data;
  }
  return res;
};

/** Extract product object from lock-status / detail-style API payloads */
export const extractProductFromResponse = (res) => {
  if (!res || typeof res !== "object") return null;
  return (
    res?.data?.product ??
    res?.product ??
    (res?.data && (res.data.lca_lock_status != null || res.data.dpp_uid != null)
      ? res.data
      : null) ??
    (res.lca_lock_status != null || res.dpp_uid != null ? res : null)
  );
};

/** Map DPP fields from a product object */
export const mapDppFromProduct = (product) => {
  if (!product || typeof product !== "object") return null;
  return {
    dpp_uid: product.dpp_uid ?? null,
    dpp_url: product.dpp_url ?? null,
    dpp_is_published: !!product.dpp_is_published,
    dpp_published_at: product.dpp_published_at ?? null,
    dpp_registry_status: product.dpp_registry_status ?? "not_registered",
    dpp_registry_id: product.dpp_registry_id ?? null,
    opencirc_passport_id: product.opencirc_passport_id ?? null,
  };
};

/** Map DPP fields from dedicated DPP status endpoint response */
export const mapDppFromStatusResponse = (res) => {
  const data = res?.data ?? res;
  if (!data) return null;
  return {
    dpp_uid: data.dpp_uid ?? null,
    dpp_url: data.dpp_url ?? null,
    dpp_is_published: !!data.is_published,
    dpp_published_at: data.dpp_published_at ?? null,
    dpp_registry_status: data.dpp_registry_status ?? "not_registered",
    dpp_registry_id: data.dpp_registry_id ?? null,
    opencirc_passport_id: data.opencirc_passport_id ?? null,
  };
};
